<template>
  <div class="MonitoringPanel d-flex">
    <aside class="MonitoringPanel__Aside flex-shrink-0 mr-3">
      <h3 class="MonitoringPanel__Title">
        {{ $t('monitoringPanel.title') }}
      </h3>
      <div class="w-75 mt-3">
        {{ $t('monitoringPanel.description') }}
      </div>
    </aside>

    <div class="MonitoringPanel__Main flex-grow-1 flex-shrink-1">
      <div class="d-flex align-items-center">
        <span class="MonitoringPanel__Label mr-3">
          {{ $t('monitoringPanel.title') }}
        </span>
        <ToggleSwitch
          class="mb-0"
          :value="data.enabled"
          @input="newValue => data.enabled = newValue"
        />
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="MonitoringPanel__Label mr-3">
          {{ $t('monitoringPanel.options.notifyEmails.title') }}
        </span>
        <b-input-group
          class="flex-shrink-1 mr-3"
        >
          <b-input
            v-model="data.notificationEmailAddresses"
            type="email"
            multiple
          />
        </b-input-group>
        <span id="emails">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="emails"
          triggers="hover"
        >
          {{ $t('monitoringPanel.options.notifyEmails.tooltip') }}
        </b-tooltip>
      </div>

      <div class="MonitoringPanel__Divider" />

      <div class="d-flex align-items-center mt-4">
        <span class="MonitoringPanel__Label mr-3">
          {{ $t('monitoringPanel.options.occupancy.title') }}
        </span>
        <ToggleSwitch
          class="mb-0"
          :value="data.thresholds.occupancy.enabled"
          @input="newValue => data.thresholds.occupancy.enabled = newValue"
        />
      </div>
      <div class="d-flex align-items-center mt-4">
        <span class="MonitoringPanel__Label mr-3">
          <!-- Bezettingsgraad grens -->
        </span>
        <b-input-group
          class="flex-shrink-1 mr-3"
          append="%"
        >
          <b-input
            v-model="data.thresholds.occupancy.value"
            type="number"
            :placeholder="`${DEFAULT_THRESHOLD.OCCUPANCY}`"
            :disabled="!data.thresholds.occupancy.enabled"
          />
        </b-input-group>
        <span class="flex-grow-1" />
        <span id="thresholdOccupancy">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="thresholdOccupancy"
          triggers="hover"
        >
          {{ $t('monitoringPanel.options.occupancy.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="MonitoringPanel__Label mr-3">
          {{ $t('monitoringPanel.options.kwh.title') }}
        </span>
        <ToggleSwitch
          class="mb-0"
          :value="data.thresholds.chargedVolume.enabled"
          @input="newValue => data.thresholds.chargedVolume.enabled = newValue"
        />
      </div>
      <div class="d-flex align-items-center mt-4">
        <span class="MonitoringPanel__Label mr-3">
          <!-- Geladen kWh grens -->
        </span>
        <b-input-group
          class="flex-shrink-1 mr-3"
          :append="$t('monitoringPanel.options.kwh.perStation')"
        >
          <b-input
            v-model="data.thresholds.chargedVolume.value"
            type="number"
            :placeholder="`${DEFAULT_THRESHOLD.CHARGED_VOLUME}`"
            :disabled="!data.thresholds.chargedVolume.enabled"
          />
        </b-input-group>
        <span id="thresholdChargedVolume">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="thresholdChargedVolume"
          triggers="hover"
        >
          {{ $t('monitoringPanel.options.kwh.tooltip') }}
        </b-tooltip>
      </div>

      <div class="d-flex align-items-center mt-4">
        <span class="MonitoringPanel__Label mr-3">
          {{ $t('monitoringPanel.options.sessions.title') }}
        </span>
        <ToggleSwitch
          class="mb-0"
          :value="data.thresholds.session.enabled"
          @input="newValue => data.thresholds.session.enabled = newValue"
        />
      </div>
      <div class="d-flex align-items-center mt-4">
        <span class="MonitoringPanel__Label mr-3">
          <!-- Sessies grens -->
        </span>
        <b-input-group
          class="flex-shrink-1 mr-3"
          :append="$t('monitoringPanel.options.sessions.perSession')"
        >
          <b-input
            v-model="data.thresholds.session.value"
            type="number"
            :placeholder="`${DEFAULT_THRESHOLD.SESSION}`"
            :disabled="!data.thresholds.session.enabled"
          />
        </b-input-group>
        <span class="flex-grow-1" />
        <span id="thresholdSession">
          <i class="far fa-fw fa-xl fa-info-circle" />
        </span>
        <b-tooltip
          target="thresholdSession"
          triggers="hover"
        >
          {{ $t('monitoringPanel.options.sessions.tooltip') }}
        </b-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import ToggleSwitch from '@/components/form/ToggleSwitch'
import { DEFAULT_THRESHOLD } from '@/../shared/valueholders/monitoring'

export default {
  name: 'MonitoringPanel',
  components: {
    ToggleSwitch,
},
  props: {
    /**
     * The data model values
     */
    values: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // The data models
      data: {
        enabled: true,
        notificationEmailAddresses: '',
        thresholds: {
          occupancy: {
            enabled: true,
            value: '',
          },
          chargedVolume: {
            enabled: true,
            value: '',
          },
          session: {
            enabled: true,
            value: '',
          },
        },
      },

      trackChanges: true,
    }
  },
  watch: {
    /**
     * Values passed via props override the data, without signaling input changes
     */
    values() {
      this.setValues()
    },
    /**
     * When any input changes, pass it on
     */
    data: {
      handler(data) {
        if (this.trackChanges) {
          this.$emit('input', data)
        }
      },
      deep: true,
    },
  },
  created() {
    this.setValues()
    this.DEFAULT_THRESHOLD = DEFAULT_THRESHOLD
  },
  methods: {
    /**
     * Set prop values as new data, without signaling input changes
     */
    setValues() {
      this.trackChanges = false
      this.data = {
        ...this.values,
      }

      this.$nextTick(() => {
        this.trackChanges = true
      })
    },
  },
}
</script>

<style lang="scss">
.MonitoringPanel {
  &__Aside {
    width: 200px;

    @media (min-width: 1280px) {
      width: 275px;
    }
  }
  &__Main {
    font-size: 1.25rem;
  }
  &__Label {
    flex-shrink: 0;
    width: 175px;
  }
  &__Divider {
    border-top: solid 1px lightgray;
    margin: 1.5rem 2rem 0 2rem;
  }
}
</style>
